/**
 * RV9 · which record to open — in the three layers a plant thinks in, the same three the
 * MBMR page shows:
 *
 *     product  →  master records (families)  →  versions
 *
 * The first layer is a grid of product cards; each names its records with the version in
 * force (the served `families`, one owner for that derivation). Opening a product, or one
 * of its records, shows the second and third: every record of the product as a section with
 * its version strip — each version in order with its state, the one in force lit — and a
 * card per version with what the designer needs to know: operations, steps, and the two
 * gaps the designer exists to close (an operation with no steps, a step nobody is assigned
 * to). Those come from the list endpoint as annotations; the card composes nothing.
 *
 * Two ways in: *Design* opens the editor; *View* opens the record read-only, end to end,
 * as the printed document a reviewer reads. The layer is in the URL (?product=&family=), so
 * a link to a product's records is a link.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Col, Dropdown, Empty, Modal, Row, Select, Skeleton, Space, Spin, Tooltip, message } from "antd";
import { AlertTriangle, Archive, ArchiveRestore, BookOpen, CheckCircle2, Copy, Download, FileText, GitBranchPlus, History, Lock, MoreHorizontal, Package, PencilRuler, Plus, Scaling, Sparkles, Upload } from "lucide-react";
import { archive_recipe, export_recipe_json, get_product_list_both, import_recipe_json } from "@/utils/api";
import CopyVersionModal from "./CopyVersionModal";
import RecordHistoryDrawer from "./RecordHistoryDrawer";

import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatCard from "@/components/StatCard";
import { useTheme } from "@/App";
import { defaultColors } from "@/themes/antd-theme";
import { listFamilies, listRecipes, submitForReview } from "./designerApi";
import { getReadiness } from "@/utils/mbmr_readiness_api";
import { FamilyList, VersionStrip, familyOfVersion, indexFamilies, stateOf } from "../Recipe/MbmrFamilies";

// 2026-10-12 · record codes read "MBMR_…" / "MBPR_…" on this page. The stored codes start "NBMR_" /
// "NBPR_" (made when a record is created, and inherited by every process, operation and step code,
// and frozen on issued batches), so they are relabelled for display only — the page talks to the
// server by record id, never by code, so nothing it saves or opens changes.
const shownCode = (v) => (typeof v === "string" ? v.replace(/^NBMR_/, "MBMR_").replace(/^NBPR_/, "MBPR_") : v);
const relabelCodes = (x) => {
  if (Array.isArray(x)) return x.map(relabelCodes);
  if (x && typeof x === "object") {
    const out = {};
    for (const [k, v] of Object.entries(x)) out[k] = typeof v === "string" ? shownCode(v) : relabelCodes(v);
    return out;
  }
  return x;
};
import "../Recipe/MbmrFamilies.css";


function initials(name) {
  return (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function num(v, unit) {
  if (v == null || v === "") return null;
  const n = Number(v);
  const t = Number.isFinite(n) ? (Number.isInteger(n) ? String(n) : String(n)) : String(v);
  return unit && unit !== "-" ? `${t} ${unit}` : t;
}

/** One word per lifecycle state and the tone it wears, matching MbmrFamilies.js's own STATE so
 *  a record reads the same in the family list and on its card. `tone` is the class the chip
 *  takes: the card used to mark only "in force", so Finalised, Under review, Returned and Draft
 *  all wore the same grey pill and the state could not be read at a glance. */
// 2026-10-06 · four working states and no more: Draft (Empty when it has no operation),
// Under review, Returned for correction, Approved — then Finalised once it is finalised.
// "Partly under review" reads as Under review and "approved, awaiting finalisation" as
// Approved: the card says where the record is, and the reason rides on the tooltips.
const STAGE = {
  finalized:           { label: "Finalised",   tone: "ok" },
  approved:            { label: "Approved",    tone: "ok" },
  under_review:        { label: "Under review", tone: "rev" },
  returned:            { label: "Returned for correction", tone: "warn" },
  draft:               { label: "Draft",       tone: "" },
  empty:               { label: "Empty",       tone: "warn" },
};
/** Any lifecycle word, as one of the card's states. */
const ONE_OF = { partially_submitted: "under_review", process_approved: "approved" };
const normalState = (s) => ONE_OF[s] || (STAGE[s] ? s : "draft");

/** States the Finalize button is offered from. */
const FINALIZABLE_STATES = ["draft", "approved"];

/** States in which the record is the controlled copy: read, never edited. A change to an
 *  approved master is a new version, which the card's ⋯ menu offers. */
const LOCKED_STATES = ["finalized", "approved"];


/**
 * Whether this record may be finalised now, in the server's words — `{available, reason}`, or
 * null while it is asked or when the question does not arise.
 *
 * Asked only of a record that could be sent at all: a draft or a returned one that has
 * operations. The answer is the lifecycle block of its readiness (`submit_available` and why
 * not): every process approved where processes went to approval, every operation configured
 * where they did not, and the record's own findings settled. The list itself does not carry
 * it — a readiness read is a tenth of a second a record, too much for 34 at once — so each
 * card asks for its own, and a product page shows a handful.
 */
function useRecordLife(r, enabled) {
  // 2026-10-06 · the record's state too, not only whether it may be finalised: the list's own
  // `lifecycle_stage` is read off the approval chain alone, so a record whose PROCESSES are with
  // their approvers (or all approved) read "Draft" on its card. The readiness lifecycle folds
  // the processes in.
  const [st, setSt] = useState(null);
  const ask = enabled && (r.operations || 0) > 0;
  useEffect(() => {
    if (!ask) { setSt(null); return undefined; }
    // What was known before this ask is dropped at once: after a Finalize the card read the old
    // "draft, may be finalised" until the answer came, and Finalize could be pressed again.
    // Meanwhile the list's own stage stands (under_review), and no Finalize is offered.
    setSt(null);
    const ctl = new AbortController();
    getReadiness({ recipe_id: r.id }, ctl.signal)
      .then(({ data }) => setSt({ state: data?.lifecycle?.state || null,
                                  available: !!data?.lifecycle?.submit_available,
                                  reason: data?.lifecycle?.submit_unavailable_reason || "" }))
      .catch(() => {});
    return () => ctl.abort();
    // `_loaded`: a reload of the list asks again — a Finalize changes the record's state (it goes
    // under review) without changing its version, and the card kept reading "Draft".
  }, [ask, r.id, r.concurrency_version, r._loaded]);
  return st;
}

/** The Submit control: a button when the record may go, a quiet "not ready" with the server's
 *  reason when it may not, nothing for a record that cannot be sent at all. */
function SubmitControl({ r, state, life: st, onSubmit, compact = false }) {
  if (!onSubmit || !st || !FINALIZABLE_STATES.includes(state)) return null;
  if (!st.available) {
    return (
      <Tooltip title={st.reason || "Not ready to finalize"}>
        <span className="dz-notready" data-testid="card-not-ready"><AlertTriangle size={12} />{compact ? null : " Not ready"}</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Finalise and send for approval">
      <Button size="small" icon={<CheckCircle2 size={13} />} onClick={() => onSubmit(r)} data-testid="card-submit"
              aria-label="Finalize">
        {compact ? null : "Finalize"}
      </Button>
    </Tooltip>
  );
}

/** The table row's Finalize: the same question the card asks, for one row. */
function RowFinalize({ r, isFinal, onSubmit }) {
  const final = isFinal(r);
  const life = useRecordLife(r, !final);
  const state = final ? "finalized" : !(r.operations > 0) ? "empty" : normalState(life?.state || r.lifecycle_stage);
  return <SubmitControl r={r} state={state} life={life} onSubmit={onSubmit} compact />;
}

/** One record, one card. The product is the section above it, so the card carries what
 *  distinguishes this record from the one beside it: batch size, use, approval, gaps. */
/** The actions the MBMR page's card offers, on this card. Copy and New version open the MBMR
 *  page's own dialog for this record (the change-control gating lives there, once); Scale goes
 *  to the scaled-copy lane with this record as the precedent; Archive is here, never delete. */
function cardActions(r, { onCopy, onVersion, onScale, onExport, onArchive, onRestore, onHistory }) {
  // 2026-10-08 · History is open to every record, in every state: it only reads.
  const history = onHistory ? [{ key: "history", icon: <History size={14} />, label: "History — created to finalised", onClick: () => onHistory(r) }, { type: "divider" }] : [];
  // 2026-10-29 (owner) · an archived record offers its history and Restore, nothing that starts new work from it.
  if (r.is_archived) {
    return [...history, { key: "restore", icon: <ArchiveRestore size={14} />, label: "Unarchive", onClick: () => onRestore(r) }];
  }
  // 2026-10-06 · Copy, New version and Scale start from a FINALISED record — the approved
  // master is what a copy or a version is taken from (the MBMR page's Copy was gated on
  // `is_finalized` too). A draft, a record under review or one returned offers Archive alone.
  if (!r.is_final) {
    return [...history, { key: "archive", icon: <Archive size={14} />, danger: true, label: "Archive…", onClick: () => onArchive(r) }];
  }
  return [
    ...history,
    { key: "copy", icon: <Copy size={14} />, label: "Copy this record", onClick: () => onCopy(r) },
    { key: "version", icon: <GitBranchPlus size={14} />, label: `New version of ${r.recipe_code || "this record"}`, onClick: () => onVersion(r) },
    { key: "scale", icon: <Scaling size={14} />, label: "New MBMR at another batch size, scaled from this one", onClick: () => onScale(r) },
    { key: "export", icon: <Download size={14} />, label: "Export JSON (full record and masters)", onClick: () => onExport(r) },
    { type: "divider" },
    { key: "archive", icon: <Archive size={14} />, danger: true, label: "Archive…", onClick: () => onArchive(r) },
  ];
}

function RecordCard({ r, onOpen, onView, onSubmit, actions }) {
  const version = r.major_version != null ? `v${r.major_version}.${r.minor_version ?? 0}` : null;
  // A record with no operation has nothing in it: "Empty", not a draft that is "complete".
  const empty = !(r.operations > 0);
  const gaps = [
    empty ? "no operations yet" : null,
    r.operations && !r.nodes ? "no steps yet" : null,
    r.unassigned ? `${r.unassigned} unassigned` : null,
  ].filter(Boolean);
  const final = !!r.is_final;
  const life = useRecordLife(r, !final);
  const state = final ? "finalized" : empty ? "empty" : normalState(life?.state || r.lifecycle_stage);
  const stage = STAGE[state].label;
  // Read-only: a finalised record (a change is a new version), one with its approvers, and one
  // approved and waiting to be finalised. The card offers View there, never a Design button
  // that refuses on click.
  const locked = final || state === "under_review" || state === "approved";
  return (
    <div className={`dz-rcard ${r.is_production ? "prod" : "trial"} ${locked ? "locked" : ""}`} role="group" aria-label={r.recipe_code}
         onClick={() => (locked ? onView(r) : onOpen(r))}>
      <div className="p">
        <span className="av">{r.product_initials}</span>
        <span className="pn" title={r.product_name}>{r.product_name}</span>
        {r.product_code ? <span className="dz-code">{r.product_code}</span> : null}
        {r.siblings > 1 ? <span className="sib">{r.siblings} records</span> : null}
      </div>
      <div className="h">
        <span className="dz-code">{r.recipe_code || `#${r.id}`}</span>
        {version ? <span className="v">{version}</span> : null}
        <span className={`dz-chip ${r.is_production ? "on" : ""}`}>{r.is_production ? "Production" : "Trial"}</span>
        {final && r.state_label
          ? <span className="dz-chip final" style={{ marginLeft: "auto" }} data-testid="version-state">{r.state_label}</span>
          : <span className={`dz-chip ${STAGE[state]?.tone || ""}`} style={{ marginLeft: "auto" }} data-testid="card-state">{stage}</span>}
        {locked ? <Lock size={12} style={{ color: "var(--dz-muted)" }} aria-label="Read-only — approved" /> : null}
        {/* The record's other actions sit with its identity, top right — in the button row they
            ran onto a line of their own once Submit joined Design and View. */}
        {actions ? (
          <span onClick={(e) => e.stopPropagation()} className="more">
            <Dropdown trigger={["click"]} menu={{ items: cardActions(r, actions) }} placement="bottomRight">
              <Button size="small" type="text" icon={<MoreHorizontal size={15} />} aria-label="More actions" data-testid="card-more" />
            </Dropdown>
          </span>
        ) : null}
      </div>
      {r.editing ? (
        <div className={`editing ${r.editing.stale ? "stale" : ""}`} data-testid="card-editing"
             title={`${r.editing.name}${r.editing.designation ? ` · ${r.editing.designation}` : ""}${r.editing.department ? ` · ${r.editing.department}` : ""} — editing since ${new Date(r.editing.since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${r.editing.stale ? " (session quiet)" : ""}`}>
          <PencilRuler size={12} /> Being edited by <b>{r.editing.name}</b>{r.editing.designation ? ` · ${r.editing.designation}` : ""}
        </div>
      ) : null}
      <div className="n" title={r.name}>{r.name || "— unnamed record"}</div>
      {r.copied_from_id ? (
        <div className="cf" data-testid="copied-from" title={`Copied from ${r.copied_from__recipe_code || r.copied_from_id}${r.copied_from__name ? ` · ${r.copied_from__name}` : ""}`}>
          copied from <code>{r.copied_from__recipe_code || `#${r.copied_from_id}`}</code>
          {r.created_by__first_name ? ` by ${r.created_by__first_name} ${r.created_by__last_name || ""}`.trimEnd() : ""}
        </div>
      ) : null}
      {num(r.batch_size, r.batch_uom) ? <div className="b">Batch {num(r.batch_size, r.batch_uom)}</div> : null}
      <div className="s">
        <span><b>{r.operations}</b><small>operation{r.operations === 1 ? "" : "s"}</small></span>
        <span><b>{r.steps}</b><small>step{r.steps === 1 ? "" : "s"}</small></span>
        <span><b>{r.approvals ? `${r.approved}/${r.approvals}` : "—"}</b><small>approved</small></span>
        <span className={gaps.length ? "gap" : "ok"}><b>{gaps.length ? gaps.length : "✓"}</b><small>{gaps.length ? gaps.join(" · ") : "complete"}</small></span>
      </div>
      <div className="a" onClick={(e) => e.stopPropagation()}>
        {/* 2026-10-29 (owner) · an archived record is read only: View, never Design or Submit. */}
        {locked || r.is_archived
          ? <Button size="small" type="primary" icon={<BookOpen size={13} />} onClick={() => onView(r)}>View</Button>
          : <>
              <Button size="small" type="primary" icon={<PencilRuler size={13} />} onClick={() => onOpen(r)}>Design</Button>
              <Button size="small" icon={<BookOpen size={13} />} onClick={() => onView(r)}>View</Button>
            </>}
        {/* 2026-10-08 · the record's life, created → finalised, one click from the card. */}
        {actions?.onHistory ? (
          <Button size="small" icon={<History size={13} />} onClick={() => actions.onHistory(r)} data-testid="card-history">History</Button>
        ) : null}
        {/* 2026-10-29 (owner) · an archived record is brought back from its own card. */}
        {r.is_archived
          ? (actions?.onRestore ? <Button size="small" icon={<ArchiveRestore size={13} />} onClick={() => actions.onRestore(r)} data-testid="card-unarchive">Unarchive</Button> : null)
          : <SubmitControl r={r} state={state} life={life} onSubmit={onSubmit} />}
      </div>
    </div>
  );
}

/** `packaging`: true lists only packaging records (MBPR), false only the others; left out, every record (/recipe-view).
 *  2026-10-29 (owner) · Master Records' Batch (MBR) and Packaging (MPR) tabs are this one page. */
export default function RecordDesignerPicker({ packaging } = {}) {
  // 2026-10-29 (owner) · an archived record leaves the active list ("Archive" did nothing visible: the list reloaded with
  // the record still on it). The header's Archived button shows the archived ones here, as the same cards, with Restore
  // (`?archived=1`); "Back to active" returns.
  const ofKind = (list) => list.filter((r) => !!r.is_archived === showArchived && (packaging === undefined || !!r.is_packaging === packaging));
  // A product's families, cut to the ones holding a listed record (the card counts and lists families).
  const famsOfKind = (fams, kept) => {
    const ids = new Set(kept.map((r) => String(r.id)));
    return (fams || []).map((p) => ({ ...p, families: (p.families || []).filter((f) => (f.versions || []).some((v) => ids.has(String(v.id)))) }));
  };
  const kindWord = packaging ? "MBPR" : "MBMR";
  const navigate = useNavigate();
  const { isDark } = useTheme();
  // RV0 D4 · theme is a token, never a literal. The KPI washes were raw Tailwind hex with no
  // dark variant, so the four tiles kept their light-mode ground on a dark page. Each shade
  // now comes from the palette that owns the meaning — the icon colour is the semantic token,
  // the wash is its 50 in light and its 900 in dark.
  const tile = useMemo(() => {
    const wash = (ramp) => (isDark ? ramp[900] : ramp[50]);
    return {
      plain: wash(defaultColors.palette.teal),
      info: wash(defaultColors.palette.steel),
      ok: wash(defaultColors.palette.green),
      bad: wash(defaultColors.palette.red),
      muted: isDark ? defaultColors.palette.gray[800] : defaultColors.palette.gray[100],
    };
  }, [isDark]);
  const [rows, setRows] = useState([]);
  const [families, setFamilies] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [params, setParams] = useSearchParams();
  const productKey = params.get("product") || null;
  const familyKey = params.get("family") ? Number(params.get("family")) : null;
  const showArchived = params.get("archived") === "1";
  const toggleArchived = () => {
    const next = new URLSearchParams(params);
    if (showArchived) next.delete("archived"); else next.set("archived", "1");
    next.delete("product"); next.delete("family");
    setParams(next);
  };
  const [picked, setPicked] = useState(null);
  // Copy / New version open their dialog here, on the list (CopyVersionModal).
  const [copying, setCopying] = useState(null);   // { record, mode: "copy" | "version" }
  const [historyOf, setHistoryOf] = useState(null);   // the record whose History drawer is open
  const goProduct = (key, family = null) => {
    const next = new URLSearchParams(params);
    if (key) next.set("product", String(key)); else next.delete("product");
    if (family) next.set("family", String(family)); else next.delete("family");
    setParams(next);
    setPicked(null);
  };
  // Cards for browsing by product; a table for scanning and sorting across records. Same
  // rows, same facts — a view preference, remembered per viewer.
  const [view, setView] = useState(() => { try { return localStorage.getItem("dz.picker") || "Cards"; } catch { return "Cards"; } });
  useEffect(() => { try { localStorage.setItem("dz.picker", view); } catch {} }, [view]);
  // Filters: the record's state (as its card names it) and its use (Production / Trial); remembered per browser.
  const [stateFilter, setStateFilter] = useState(() => { try { return JSON.parse(localStorage.getItem("dz.picker.state") || "[]"); } catch { return []; } });
  const [useFilter, setUseFilter] = useState(() => { try { return localStorage.getItem("dz.picker.use") || null; } catch { return null; } });
  useEffect(() => { try { localStorage.setItem("dz.picker.state", JSON.stringify(stateFilter)); } catch {} }, [stateFilter]);
  useEffect(() => { try { if (useFilter) localStorage.setItem("dz.picker.use", useFilter); else localStorage.removeItem("dz.picker.use"); } catch {} }, [useFilter]);
  const filtering = stateFilter.length > 0 || !!useFilter;

  useEffect(() => {
    let alive = true;
    // Switching between the active and the archived list shows the loading state, never the other list's cards.
    setLoading(true); setRows([]);
    Promise.all([listRecipes(), listFamilies().catch(() => [])])
      .then(([data, fams]) => {
        if (!alive) return;
        const kept = ofKind(relabelCodes(data || []));
        setRows(kept); setFamilies(indexFamilies(famsOfKind(relabelCodes(fams), kept)));
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  // the state the card shows, from the list itself (finalised · empty · the lifecycle word) — filter and State column
  const rowState = (r) => {
    const v = familyOfVersion(families, r.product_id, r.id)?.versions?.find((x) => String(x.id) === String(r.id));
    if (r.is_final || ["finalized", "approved"].includes(v?.state) || LOCKED_STATES.includes(r.lifecycle_stage)) {
      return r.lifecycle_stage === "approved" && !r.is_final ? "approved" : "finalized";
    }
    return !(r.operations > 0) ? "empty" : normalState(r.lifecycle_stage);
  };
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hit = (r) => (!q || [r.recipe_code, r.name, r.product_name, r.product_code]
      .some((v) => (v || "").toLowerCase().includes(q)))
      && (!stateFilter.length || stateFilter.includes(rowState(r)))
      && (!useFilter || (useFilter === "production" ? !!r.is_production : !r.is_production));
    const by = new Map();
    rows.filter(hit).forEach((r) => {
      // Support records have no product; they are a group of their own, said plainly.
      const key = r.product_id || "support";
      if (!by.has(key)) by.set(key, { key, name: r.product_name || "Support records",
                                       code: r.product_code || "", records: [],
                                       families: (families[r.product_id]?.families || []) });
      by.get(key).records.push(r);
    });
    // A product whose families the search did not reach still lists them all: the search
    // narrows products, and a record is opened from its product.
    return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, query, families, stateFilter, useFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = useMemo(() => groups.find((g) => String(g.key) === String(productKey)) || null, [groups, productKey]);
  // Level 2: each family of the product with the rows (version cards) that belong to it;
  // rows in no family (nothing the families service knows) stay visible as their own section.
  const sections = useMemo(() => {
    if (!current) return [];
    const byId = new Map(current.records.map((r) => [String(r.id), r]));
    const used = new Set();
    const out = current.families
      .filter((f) => !familyKey || f.root_id === familyKey)
      .map((f) => {
        const versions = f.versions.map((v) => byId.get(String(v.id))).filter(Boolean);
        versions.forEach((r) => used.add(String(r.id)));
        return { family: f, versions };
      });
    const rest = current.records.filter((r) => !used.has(String(r.id)));
    if (rest.length && !familyKey) out.push({ family: null, versions: rest });
    // a filter hides the families none of whose versions match it
    return filtering ? out.filter((x) => x.versions.length) : out;
  }, [current, familyKey, filtering]);

  const open = (r) => navigate(`/record-designer/${r.id}`);
  // an approved / finalised record opens as its paper view; one under review opens read-only in the designer
  const view_ = (r) => navigate(isFinal(r) ? `/record-designer/${r.id}/view` : `/record-designer/${r.id}`);
  const reload = () => {
    const at = Date.now();
    Promise.all([listRecipes(), listFamilies().catch(() => [])])
      .then(([data, fams]) => {
        const kept = ofKind(relabelCodes(data || []));
        setRows(kept.map((x) => ({ ...x, _loaded: at }))); setFamilies(indexFamilies(famsOfKind(relabelCodes(fams), kept)));
      });
  };
  // RV9s · the MBMR page's card actions, from here. Copy and New version are that page's
  // own dialogs (it opens them when sent a record and an action); Scale arrives prefilled.
  const actions = {
    // 2026-10-06 · their dialog opens here, on the list; the person stays where they are and
    // the list reloads once the copy or the version exists.
    onCopy: (r) => setCopying({ record: r, mode: "copy" }),
    onVersion: (r) => setCopying({ record: r, mode: "version" }),
    onHistory: (r) => setHistoryOf(r),
    onScale: (r) => navigate(`/scaled-copy?product=${r.product_id || ""}&precedent=${r.id}${r.batch_uom ? `&batch_uom=${encodeURIComponent(r.batch_uom)}` : ""}`),
    onArchive: (r) => {
      // A master record is never deleted — archived, with the same call the MBMR page makes.
      const hide = message.loading(`Archiving ${r.recipe_code || r.name}…`, 0);
      archive_recipe({ recipe_id: r.id, status: true }, (res) => {
        hide();
        if (res?.status === "success") { message.success(res?.message || "Archived."); reload(); }
        else message.warning(res?.message || "Could not archive this record.");
      });
    },
    // Back to the active list, with the same call (status false).
    onRestore: (r) => {
      const hide = message.loading(`Restoring ${r.recipe_code || r.name}…`, 0);
      archive_recipe({ recipe_id: r.id, status: false }, (res) => {
        hide();
        if (res?.status === "success") { message.success(`${r.recipe_code || r.name} restored to the active list.`); reload(); }
        else message.warning(res?.message || "Could not restore this record.");
      });
    },
  };
  const confirmArchive = (r) => Modal.confirm({
    title: `Archive ${r.recipe_code || r.name}?`,
    content: "It leaves the active lists and stays readable, with its history. Nothing is deleted.",
    okText: "Archive", okButtonProps: { danger: true },
    onOk: () => actions.onArchive(r),
  });
  const confirmRestore = (r) => Modal.confirm({
    title: `Unarchive ${r.recipe_code || r.name}?`,
    content: "It returns to the active list as it was archived.",
    okText: "Unarchive",
    onOk: () => actions.onRestore(r),
  });
  const exportJson = (r) => {
    const hide = message.loading("Preparing the JSON file…", 0);
    export_recipe_json(r.id, (res) => {
      hide();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([JSON.stringify(res.data)], { type: "application/json" }));
      link.download = `Recipe_${String(r.recipe_code || r.name).replace(/[^\w.-]+/g, "_")}_${r.id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      message.success("JSON exported");
    }, () => { hide(); message.error("JSON export failed"); });
  };
  const cardOps = { ...actions, onExport: exportJson, onArchive: confirmArchive, onRestore: confirmRestore };
  // Import into the open product: the masters in the file are matched by name, the missing ones
  // created (backend services/recipe/portable.py), and the person is told which.
  const importInput = useRef(null);
  const importTarget = useRef(null);
  const [importing, setImporting] = useState(false);
  const chooseImportFile = (target) => { importTarget.current = target; importInput.current?.click(); };
  // A product with no record has no card, so the page-level Import is how a record reaches it.
  const [pickingProduct, setPickingProduct] = useState(false);
  const [importProducts, setImportProducts] = useState([]);
  const [importProduct, setImportProduct] = useState(null);
  const openImport = () => {
    // Inside a product there is nothing to ask: the record lands on the product that is open.
    if (current) { chooseImportFile(current.key); return; }
    setImportProduct(null);
    setPickingProduct(true);
    get_product_list_both({ module_type: packaging ? "pack" : "prod", recipe_type: false }, (res) => setImportProducts(res?.data?.data || []));
  };
  const showImported = (data) => {
    const report = data?.report || {};
    const created = Object.entries(report.masters_created || {});
    const reused = Object.values(report.masters_reused || {}).reduce((n, x) => n + x, 0);
    const warnings = report.warnings || [];
    Modal.success({
      title: `Imported as draft: ${data?.new_recipe_name}`,
      width: 620,
      zIndex: 10100,
      content: (
        <div style={{ fontSize: 14, maxHeight: "55vh", overflowY: "auto" }}>
          <p>{reused} master{reused === 1 ? "" : "s"} matched by name and reused.</p>
          {created.length ? <p style={{ marginBottom: 4 }}><b>New masters created</b></p> : <p>No new master was needed.</p>}
          {created.map(([table, names]) => <p key={table} style={{ margin: "0 0 4px" }}>{table} ({names.length}): {names.slice(0, 12).join(", ")}{names.length > 12 ? " …" : ""}</p>)}
          {warnings.length ? <p style={{ margin: "12px 0 4px" }}><b>Needs a look</b></p> : null}
          {warnings.map((w) => <p key={w} style={{ margin: "0 0 4px" }}>{w}</p>)}
        </div>
      ),
    });
  };
  const importJson = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    file.text().then((text) => {
      const parsed = JSON.parse(text);
      import_recipe_json(
        importTarget.current === "support" ? { recipe_data: parsed, support_mbmr: true } : { recipe_data: parsed, product_id: importTarget.current },
        (res) => {
          setImporting(false);
          if (res?.data?.status === "success") { showImported(res.data.data); reload(); } else { message.error(res?.data?.message || "Import failed"); }
        },
        (err) => { setImporting(false); message.error(err?.response?.data?.message || "Import failed"); },
      );
    }).catch(() => { setImporting(false); message.error("This is not a valid JSON file"); });
  };
  // Finalised: through the MBMR page's flow (`Recipe_Status`, the family reads "finalized") or
  // through the designer's approval chain ("approved"). The list's own lifecycle is the
  // fallback for a record the family index does not hold.
  const isFinal = (r) => {
    const v = familyOfVersion(families, r.product_id, r.id)?.versions?.find((x) => String(x.id) === String(r.id));
    return ["finalized", "approved"].includes(v?.state) || LOCKED_STATES.includes(r.lifecycle_stage);
  };
  // Finalise: send the record into its approval chain — the same call as the designer's
  // "Submit for review". A record with blocking gaps is refused by the server with the reason,
  // and the author is offered the designer, where the gaps are listed.
  const submit = (r) => Modal.confirm({
    title: `Finalize ${r.recipe_code || r.name}?`,
    content: "The record goes to its approvers and is finalised when the last one signs. It is read-only until then, or until it is returned for correction.",
    okText: "Finalize",
    onOk: async () => {
      try {
        const res = await submitForReview(r.id);
        if (res?.status === "success") {
          message.success(res.message || "Submitted for approval.");
          // At once, before the list answers: the card reads Under review and offers no Finalize.
          setRows((rows) => rows.map((x) => (x.id === r.id ? { ...x, lifecycle_stage: "under_review", _loaded: Date.now() } : x)));
          reload();
          return;
        }
        throw new Error(res?.message);
      } catch (err) {
        Modal.warning({
          title: "This record cannot be finalized yet",
          content: err?.response?.data?.message || err?.message || "The record could not be submitted.",
          okText: "Review issues in the designer", closable: true,
          onOk: () => open(r),
        });
      }
    },
  });
  // New MBMR for a product: from scratch (the creation form, product filled in) or scaled from
  // one of its records (the scaled-copy lane, product and precedent filled in).
  const newRecordItems = (g, precedent) => [
    { key: "new", icon: <Plus size={14} />, label: `New ${g.key === "support" ? "support record" : `${kindWord} for this product`} from scratch`,
      onClick: () => navigate("/recipeMetadata", { state: g.key === "support" ? { support_mbmr: true } : { product_id: g.key } }) },
    ...(precedent ? [{ key: "scale", icon: <Scaling size={14} />, label: `New ${kindWord} at another batch size, scaled from ${precedent.code || precedent.name}`,
      onClick: () => navigate(`/scaled-copy?product=${g.key}&precedent=${precedent.id}${precedent.batch_uom ? `&batch_uom=${encodeURIComponent(precedent.batch_uom)}` : ""}`) }] : []),
  ];
  const precedentOf = (g) => {
    const fam = (g.families || []).find((f) => f.effective) || (g.families || [])[0];
    const v = fam ? (fam.effective || fam.latest) : null;
    return v ? { id: v.id, code: v.code, name: fam.name, batch_uom: v.batch_uom } : (g.records[0] ? { id: g.records[0].id, code: g.records[0].recipe_code, name: g.records[0].name, batch_uom: g.records[0].batch_uom } : null);
  };

  const approvedCount = rows.filter((r) => r.approvals && r.approved === r.approvals).length;
  const gapCount = rows.filter((r) => (r.operations && !r.nodes) || r.unassigned).length;
  const productCount = new Set(rows.map((r) => r.product_id || "support")).size;

  return (
    <div className="p-2.5 pt-5 dz dz-picker" style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : defaultColors.palette.gray[50] }}>
      <Card
        bordered={false}
        className="border border-gray-300 shadow-md"
        classNames={{ body: "page-card-scroll" }}
        style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : "#ffffff", height: "calc(100dvh - 120px)", display: "flex", flexDirection: "column" }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 24px 24px" } }}
        title={
          <PageHeader
            compact
            icon={<FileText size={22} />}
            title={showArchived ? "Archived records" : "Records"}
            view={{ value: view, onChange: setView, options: ["Cards", "Table"] }}
            filters={(
              <Space size={8} wrap>
                <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="State" style={{ minWidth: 160 }}
                  value={stateFilter} onChange={setStateFilter} data-testid="picker-filter-state"
                  options={["finalized", "approved", "under_review", "returned", "draft", "empty"].map((k) => ({ value: k, label: STAGE[k].label }))} />
                <Select allowClear placeholder="Production / Trial" style={{ width: 170 }} value={useFilter || undefined}
                  onChange={(v) => setUseFilter(v || null)} data-testid="picker-filter-use"
                  options={[{ value: "production", label: "Production" }, { value: "trial", label: "Trial" }]} />
              </Space>
            )}
            search={{ value: query, onChange: (e) => setQuery(e.target.value), onSearch: (v) => setQuery(v), placeholder: "Search products, records, codes...", width: 250 }}
            secondary={[showArchived
              ? { key: "active", label: "Back to active", icon: <FileText size={14} />, onClick: toggleArchived }
              : { key: "archived", label: "Archived", icon: <Archive size={14} />, onClick: toggleArchived },
              ...(showArchived ? [] : [{ key: "import", label: "Import", icon: <Upload size={14} />, onClick: openImport }, { key: "from-document", label: "From a document", icon: <Sparkles size={14} />, onClick: () => navigate("/recipe-view/from-document") },
                { key: "sop-change-impact", label: "SOP change impact", icon: <Sparkles size={14} />, onClick: () => navigate("/recipe-view/sop-change-impact") }])]}
            // The archived list is read only: nothing is created from it.
            primary={showArchived ? undefined : { label: "New master record", icon: <Plus size={14} />, onClick: () => navigate("/recipeMetadata") }}
          />
        }
      >
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <StatCard label="Records" value={rows.length} icon={<FileText size={24} />} bgColor={tile.plain} iconColor={defaultColors.primary} />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard label="Products" value={productCount} icon={<Package size={24} />} bgColor={tile.info} iconColor={defaultColors.info} />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard label="Fully approved" value={approvedCount} icon={<CheckCircle2 size={24} />} bgColor={tile.ok} iconColor={defaultColors.success} />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard label="With gaps to close" value={gapCount} icon={<AlertTriangle size={24} />}
                    bgColor={gapCount ? tile.bad : tile.muted} iconColor={gapCount ? defaultColors.error : defaultColors.palette.gray[500]} />
        </Col>
      </Row>

      {loading ? <Skeleton active paragraph={{ rows: 6 }} /> : null}
      {!loading && !groups.length ? (
        <Empty description={filtering ? "No record matches these filters." : "No record matches."}>
          {filtering ? <Button size="small" onClick={() => { setStateFilter([]); setUseFilter(null); }}>Clear filters</Button> : null}
        </Empty>
      ) : null}

      {!loading && view === "Table" && groups.length ? (
        <DataTable
          tableKey="recordPickerTable"
          size="small"
          rowKey="id"
          bordered
          columnConfig
          showCount={false}
          height={null}
          scrollX="max-content"
          className="dz-picktable"
          data={groups.flatMap((g) => g.records.map((r) => ({
            ...r,
            product_name: g.name,
            product_code: g.code,
            version_label: r.major_version != null ? `v${r.major_version}.${r.minor_version ?? 0}` : "—",
            version_sort: (r.major_version || 0) * 1000 + (r.minor_version || 0),
            use_label: r.is_production ? "Production" : "Trial",
            state_key: rowState(r),
            state_label: STAGE[rowState(r)].label,
            gap_sort: ((!(r.operations > 0)) ? 4 : 0) + (r.operations && !r.nodes ? 2 : 0) + (r.unassigned ? 1 : 0),
            approved_sort: r.approved || 0,
          })))}
          onRow={(r) => ({ onDoubleClick: () => open(r) })}
          columns={[
            { title: "Product", key: "product_name", dataIndex: "product_name", sortType: "string", width: 300,
              render: (v, r) => <Space size={6}><span>{v}</span>{r.product_code ? <span className="dz-code">{r.product_code}</span> : null}</Space> },
            { title: "Code", key: "recipe_code", dataIndex: "recipe_code", sortType: "string", width: 150,
              render: (v, r) => <span className="dz-code">{v || `#${r.id}`}</span> },
            { title: "Record", key: "name", dataIndex: "name", sortType: "string", width: 320 },
            { title: "Version", key: "version_label", dataIndex: "version_sort", sortType: "number", width: 100, render: (_, r) => r.version_label },
            { title: "State", key: "state_label", dataIndex: "state_label", filterable: true, sortType: "string", width: 190,
              render: (v, r) => <span className={`dz-chip ${STAGE[r.state_key]?.tone || ""}`} data-testid="row-state">{v}</span> },
            { title: "Use", key: "use_label", dataIndex: "use_label", filterable: true, width: 110,
              render: (v, r) => <span className={`dz-chip ${r.is_production ? "on" : ""}`}>{v}</span> },
            { title: "Operations", key: "operations", dataIndex: "operations", sortType: "number", width: 120, align: "right" },
            { title: "Steps", key: "steps", dataIndex: "steps", sortType: "number", width: 90, align: "right" },
            { title: "Gaps", key: "gaps", dataIndex: "gap_sort", sortType: "number", width: 180,
              render: (_, r) => {
                const gaps = [!(r.operations > 0) ? "empty — no operations yet" : null, r.operations && !r.nodes ? "no steps yet" : null, r.unassigned ? `${r.unassigned} unassigned` : null].filter(Boolean);
                return gaps.length ? <span style={{ color: "var(--dz-block)" }}>{gaps.join(" · ")}</span> : <span style={{ color: "var(--dz-ok)" }}>complete</span>;
              } },
            { title: "Approved", key: "approved", dataIndex: "approved_sort", sortType: "number", width: 110, render: (_, r) => (r.approvals ? `${r.approved}/${r.approvals}` : "—") },
            { title: "Batch", key: "batch", dataIndex: "batch_size", width: 120, render: (_, r) => num(r.batch_size, r.batch_uom) || "—" },
            { title: "Actions", key: "actions", width: 250, fixed: "right", alwaysVisible: true, render: (_, r) => (
                <Space size={4}>
                  {/* as on the card: a finalised, approved or under-review record is read, never designed */}
                  {["finalized", "approved", "under_review"].includes(r.state_key) || r.is_archived ? null
                    : <Button size="small" type="primary" icon={<PencilRuler size={13} />} onClick={() => open(r)}>Design</Button>}
                  <Button size="small" icon={<BookOpen size={13} />} onClick={() => view_(r)}>View</Button>
                  {r.is_archived
                    ? <Button size="small" icon={<ArchiveRestore size={13} />} onClick={() => cardOps.onRestore(r)}>Unarchive</Button>
                    : <RowFinalize r={r} isFinal={isFinal} onSubmit={submit} />}
                  <Dropdown trigger={["click"]} menu={{ items: cardActions({ ...r, is_final: isFinal(r) }, cardOps) }}>
                    <Button size="small" icon={<MoreHorizontal size={14} />} aria-label="More actions" />
                  </Dropdown>
                </Space>) },
          ]}
        />
      ) : null}

      {view === "Table" || loading ? null : !current ? (
      /* Layer 1 · products. Each card names its records with the version in force. */
      <div className="dz-products" data-testid="product-grid">
        {groups.map((g) => {
          const fams = g.families;
          const inForce = fams.filter((f) => f.effective).length;
          const gaps = g.records.filter((r) => (r.operations && !r.nodes) || r.unassigned).length;
          const editing = g.records.map((r) => r.editing).find(Boolean);
          return (
            <div key={g.key} className="dz-pcard" role="group" aria-label={g.name} data-testid="product-card">
              <div className="p" onClick={() => goProduct(g.key)}>
                <span className="av">{initials(g.name)}</span>
                {/* One line, cut with "…" when long; the whole name on hover. */}
                <Tooltip title={g.name} placement="topLeft" mouseEnterDelay={0.3}>
                  <span className="pn">{g.name}</span>
                </Tooltip>
                {g.code ? <span className="dz-code">{g.code}</span> : null}
              </div>
              <div className="s" onClick={() => goProduct(g.key)}>
                <span><b>{fams.length || g.records.length}</b><small>{fams.length === 1 ? kindWord : `${kindWord}s`}</small></span>
                <span><b>{inForce}</b><small>in force</small></span>
                <span><b>{g.records.length}</b><small>version{g.records.length === 1 ? "" : "s"}</small></span>
                <span className={gaps ? "gap" : "ok"}><b>{gaps || "✓"}</b><small>{gaps ? `with gaps` : "complete"}</small></span>
              </div>
              {editing ? (
                <div className="editing" data-testid="card-editing" title={`${editing.name}${editing.designation ? ` · ${editing.designation}` : ""} — editing since ${new Date(editing.since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}>
                  <PencilRuler size={12} /> Being edited by <b>{editing.name}</b>{editing.designation ? ` · ${editing.designation}` : ""}
                </div>
              ) : null}
              {fams.length ? (
                <FamilyList families={fams} onOpen={(f) => goProduct(g.key, f.root_id)} muted="var(--dz-muted)" ink="var(--dz-ink)" />
              ) : (
                <div className="mbmr-families"><div className="mbmr-families-head" style={{ color: "var(--dz-muted)" }}>{g.records.length} record{g.records.length === 1 ? "" : "s"} · no family data</div></div>
              )}
              <div className="a">
                <Button size="small" type="primary" onClick={() => goProduct(g.key)}>Open {fams.length === 1 ? "the record" : "records"}</Button>
                {showArchived ? null : (
                  <Dropdown trigger={["click"]} menu={{ items: newRecordItems(g, precedentOf(g)) }}>
                    <Button size="small" icon={<Plus size={13} />} data-testid="product-new">New {kindWord}</Button>
                  </Dropdown>
                )}
              </div>
            </div>
          );
        })}
      </div>
      ) : (
      /* Layers 2 and 3 · one product: its records, each with its versions. */
      <div className="dz-product-open" data-testid="product-open">
        <div className="dz-product-head">
          <button type="button" className="dz-backlink" onClick={() => goProduct(null)}>← All products</button>
          <span className="av">{initials(current.name)}</span>
          <span className="pn">{current.name}</span>
          {current.code ? <span className="dz-code">{current.code}</span> : null}
          <span className="dz-chip">{current.families.length || current.records.length} {current.families.length === 1 ? kindWord : `${kindWord}s`}</span>
          <span className="dz-chip ok">{current.families.filter((f) => f.effective).length} in force</span>
          {familyKey ? <button type="button" className="dz-backlink" onClick={() => goProduct(current.key)}>All {kindWord}s of this product</button> : null}
          {showArchived ? null : (
            <>
              <Button size="small" icon={<Upload size={13} />} loading={importing} data-testid="product-import" style={{ marginLeft: "auto" }}
                      onClick={() => chooseImportFile(current.key)}>Import</Button>
              <Dropdown trigger={["click"]} menu={{ items: newRecordItems(current, precedentOf(current)) }}>
                <Button size="small" type="primary" icon={<Plus size={13} />} data-testid="product-new">New {kindWord}</Button>
              </Dropdown>
            </>
          )}
        </div>
        {sections.map(({ family, versions }) => (
          <section key={family ? family.root_id : "rest"} className="dz-family" data-testid="family-section">
            {family ? (
              <VersionStrip family={family} currentId={picked} onPick={(v) => setPicked(v.id)} />
            ) : (
              <div className="dz-family-rest">Records the family view does not know (support or packaging records)</div>
            )}
            <div className="dz-records">
              {versions
                .sort((a, b) => (a.major_version || 0) - (b.major_version || 0) || (a.minor_version || 0) - (b.minor_version || 0))
                .map((r) => {
                  const v = family?.versions.find((x) => String(x.id) === String(r.id));
                  return (
                    <div key={r.id} className={`dz-rcard-wrap ${v?.effective ? "eff" : ""} ${String(picked) === String(r.id) ? "on" : ""}`} data-testid="version-card">
                      <RecordCard r={{ ...r, product_name: current.name, product_code: current.code, product_initials: initials(current.name),
                                       siblings: versions.length,
                                       // 2026-10-07 · an approved record reads "Finalised" on its card (the
                                       // family strip still marks which version is in force).
                                       state_label: v ? (v.effective ? "Finalised" : stateOf(v).label) : null, state_effective: !!v?.effective,
                                       is_final: isFinal(r) }}
                                  onOpen={open} onView={view_} onSubmit={submit} actions={cardOps} />
                    </div>
                  );
                })}
            </div>
          </section>
        ))}
        {!sections.length ? <Empty description="No record on this product." /> : null}
      </div>
      )}
      </Card>
      <input ref={importInput} type="file" accept=".json" style={{ display: "none" }} onChange={importJson} data-testid="product-import-file" />
      <Modal open={pickingProduct} centered title="Import a record" width={480} zIndex={10100} onCancel={() => setPickingProduct(false)}
             okText="Choose JSON file" okButtonProps={{ disabled: !importProduct, "data-testid": "import-choose-file" }}
             onOk={() => { setPickingProduct(false); chooseImportFile(importProduct); }}>
        <div style={{ fontSize: 14, marginBottom: 8 }}>The record and its MFR are created on this product.</div>
        <Select showSearch optionFilterProp="label" placeholder="Select product" style={{ width: "100%" }} value={importProduct} onChange={setImportProduct}
                data-testid="import-product" getPopupContainer={(el) => el.parentElement}
                options={[...importProducts.map((p) => ({ value: p.product_id, label: p.product_name })),
                          ...(packaging ? [] : [{ value: "support", label: "Support records" }])]} />
      </Modal>
      <Modal open={importing} centered closable={false} maskClosable={false} keyboard={false} footer={null} width={340} zIndex={10100}>
        <div data-testid="import-progress" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "20px 0", textAlign: "center" }}>
          <Spin size="large" />
          <div style={{ fontSize: 16, fontWeight: 600 }}>Import in progress</div>
          <div style={{ fontSize: 14 }}>Please wait, do not close this page.</div>
        </div>
      </Modal>
      <RecordHistoryDrawer recipe={historyOf} open={!!historyOf} onClose={() => setHistoryOf(null)} />
      <CopyVersionModal record={copying?.record} mode={copying?.mode} onClose={() => setCopying(null)}
                        onDone={() => { setCopying(null); reload(); }} />
    </div>
  );
}
